import React, { useState, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import DraggableList from '../DraggableList';

import AccountService from '../../../services/AccountService';
import LenderService from '../../../services/LenderService';
import { useServicerStore } from '../../../store';

function areValueArraysEqual(dlValue, value) {
  // Check if the lengths are different
  if (dlValue?.length !== value?.length) {
    return false;
  }

  // Compare corresponding elements
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < dlValue?.length; i++) {
    if (dlValue[i].value !== value[i].account_id) {
      return false;
    }
  }

  // If all elements match in order, the arrays are considered equal
  return true;
}

export default function PaymentWaterfall({
  value,
  tabindex,
  isVisible,
  setFieldValue,
  name,
  label,
  lenderId,
  readOnly,
}) {
  const [draggableListValue, setDraggableListValue] = useState([]);

  const { id } = useParams();

  const currentLenderId = lenderId || id;

  const userRole = useServicerStore((state) => state.userRole);
  const isLender = userRole === 'lender';

  const { data: lender } = useQuery({
    queryKey: ['lender', currentLenderId],
    queryFn: () => LenderService.getLender(currentLenderId),
    enabled: !!currentLenderId,
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: AccountService.getAccounts,
  });

  const options = accounts?.map((account) => ({
    value: account.id,
    label: account.name,
  }));

  const lenderOptions = lender?.default_payment_waterfall?.map((el) => {
    return {
      value: el.account_id,
      label: el._account?.name,
    };
  });

  // Set inital value of draggable list based on lender
  useEffect(() => {
    if (
      !lender ||
      !lender?.default_payment_waterfall?.length ||
      !accounts ||
      value?.length
    ) {
      return;
    }

    const dlValue = lender.default_payment_waterfall
      .sort((a, b) => a.sequence - b.sequence)
      .map((el) => {
        const account = accounts.find((acc) => acc.id === el.account_id);
        return {
          value: account?.id,
          label: account?.name,
        };
      });

    setDraggableListValue(dlValue);
  }, [lender, accounts, value?.length]);

  // Set value of draggable list when formik value is set or changed
  useEffect(() => {
    if ((!accounts || !value?.length) && !isLender) {
      return;
    }

    const dlValue = isLender
      ? lender?.default_payment_waterfall
          .toSorted((a, b) => a.sequence - b.sequence)
          .map((el) => {
            return {
              value: el.account_id,
              label: el?._account?.name,
            };
          })
      : value
          .toSorted((a, b) => a.sequence - b.sequence)
          .map((el) => {
            const account = accounts.find((acc) => acc.id === el.account_id);
            return {
              value: account?.id,
              label: account?.name,
            };
          });

    const areEqual = areValueArraysEqual(dlValue, value);

    if (!areEqual || !draggableListValue?.length) {
      setDraggableListValue(dlValue);
    }
  }, [
    accounts,
    draggableListValue?.length,
    isLender,
    lender?.default_payment_waterfall,
    value,
  ]);

  // Update formik value
  useEffect(() => {
    setFieldValue(
      name,
      draggableListValue?.map((el, index) => ({
        account_id: el.value,
        sequence: index + 1,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggableListValue]);

  return (
    <div>
      <h3 className="block text-sm font-medium mb-1">{label}</h3>
      <DraggableList
        options={isLender ? lenderOptions : options}
        value={draggableListValue}
        setValue={setDraggableListValue}
        tabindex={tabindex}
        isVisible={isVisible}
        readOnly={readOnly}
      />
    </div>
  );
}
